import * as React from 'react';

import { ContentBanner, ContentBannerAutoHide } from '@xing-com/banner';
import { Button } from '@xing-com/button';

import { Styled } from '../../components/slide-up-banner.styles';
import placements from '../../placements';
import { setFrequencyCapping } from '../../utils/frequency-capping';
import { openURLbyTarget } from '../../utils/open-url-by-target';
import { TrackingLibrary } from '../../utils/tracking';
import withBraze from '../../utils/with-braze';

const PLACEMENT = placements.SLIDE_UP;

interface SlideUpPayload {
  url?: string;
  campaignId: string;
  messageDuration: number;
  dismissType: string;
  message: string;
  clickAction: string;
  openTarget: string;
  imageUrl?: string;
  altImage?: string;
  buttonText?: string;
  title?: string;
}

interface SlideUpProps {
  payload: SlideUpPayload;
  close: () => void;
  section: string;
  trackingLibrary?: TrackingLibrary;
}

const SlideUp = ({
  payload: {
    messageDuration,
    url,
    message,
    campaignId,
    openTarget,
    clickAction,
    imageUrl,
    title,
    altImage,
    buttonText,
    dismissType,
  },
  close,
  section,
  trackingLibrary,
}: SlideUpProps): JSX.Element => {
  const onSlideUpClick = (): void => {
    trackingLibrary?.trackInAppMessageClick(PLACEMENT, campaignId);
    if (clickAction === 'URI' && url) {
      openURLbyTarget(url, openTarget);
    }
    close();
  };

  const onSlideUpContentShown = (): void => {
    trackingLibrary?.trackInAppMessageImpression(PLACEMENT, campaignId);
    setFrequencyCapping(section, PLACEMENT);
  };

  React.useEffect(onSlideUpContentShown, [campaignId]);

  const content = (
    <Styled.SlideUpContent>
      {imageUrl && <Styled.Icon src={imageUrl} alt={altImage} />}
      <Styled.ContentBox>
        <Styled.Text data-test-id={'slide-up-text'}>{message}</Styled.Text>
        {buttonText && (
          <Button onClick={onSlideUpClick} data-qa={'slide-up-button'}>
            {buttonText}
          </Button>
        )}
      </Styled.ContentBox>
    </Styled.SlideUpContent>
  );

  const autoDismiss = dismissType === 'AUTO_DISMISS';

  return autoDismiss ? (
    <ContentBannerAutoHide
      {...(title ? { 'aria-label': title } : {})}
      data-qa="slideup-portal-container"
      handleOnClose={close}
      timeout={messageDuration}
      variant="default"
      display="fixed"
      noPadding={true}
      show={true}
      showCloseButton={true}
    >
      {content}
    </ContentBannerAutoHide>
  ) : (
    <ContentBanner
      {...(title ? { 'aria-label': title } : {})}
      data-qa="slideup-portal-container"
      handleOnClose={close}
      variant="default"
      display="fixed"
      noPadding={true}
      showCloseButton={true}
    >
      {content}
    </ContentBanner>
  );
};

const SlideUpConsumer = withBraze(PLACEMENT)(SlideUp);
export { SlideUpConsumer, SlideUp, SlideUpProps };
